/** Allowed file type definitions for form uploads */
export interface AllowedFileType {
  mime: string;
  extensions: string[];
  maxSizeMB: number;
  category: 'document' | 'image' | 'cad';
}

export const ALLOWED_FILE_TYPES: AllowedFileType[] = [
  // Documents
  { mime: 'application/pdf', extensions: ['.pdf'], maxSizeMB: 10, category: 'document' },
  { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extensions: ['.docx'], maxSizeMB: 10, category: 'document' },
  // Images
  { mime: 'image/png', extensions: ['.png'], maxSizeMB: 10, category: 'image' },
  { mime: 'image/jpeg', extensions: ['.webp', '.jpeg'], maxSizeMB: 10, category: 'image' },
  // CAD formats
  { mime: 'application/step', extensions: ['.step', '.stp'], maxSizeMB: 20, category: 'cad' },
  { mime: 'application/dxf', extensions: ['.dxf'], maxSizeMB: 20, category: 'cad' },
  { mime: 'application/dwg', extensions: ['.dwg'], maxSizeMB: 20, category: 'cad' },
];

export const MAX_FILES = 5;
export const MAX_TOTAL_SIZE_MB = 50;

export const ACCEPTED_MIME_TYPES = ALLOWED_FILE_TYPES.map((t) => t.mime);
export const ACCEPTED_EXTENSIONS = ALLOWED_FILE_TYPES.flatMap((t) => t.extensions);

export const STORAGE_BUCKET = 'form-attachments';

/**
 * Returns the max size in bytes for a given file based on extension.
 * Falls back to 10 MB if unknown.
 */
export function getMaxSizeForFile(file: File): number {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  const match = ALLOWED_FILE_TYPES.find((t) => t.extensions.includes(ext));
  return (match?.maxSizeMB ?? 10) * 1024 * 1024;
}

export type FileValidationError =
  | { code: 'invalid-type'; ext: string; allowed: string }
  | { code: 'too-large'; fileName: string; maxSizeMB: number };

/**
 * Validates a single file against the whitelist.
 * Returns a structured error or null. The caller translates to a user-facing string.
 */
export function validateFile(file: File): FileValidationError | null {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  const match = ALLOWED_FILE_TYPES.find((t) => t.extensions.includes(ext));

  if (!match) {
    return { code: 'invalid-type', ext, allowed: ACCEPTED_EXTENSIONS.join(', ') };
  }

  const maxBytes = match.maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return { code: 'too-large', fileName: file.name, maxSizeMB: match.maxSizeMB };
  }

  return null;
}

/**
 * Format bytes to human-readable string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
